import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "alloyFilter",
    "alloyGroupFilter",
    "alloyGroupOption",
    "alloyOption",
    "applicationFilter",
    "applicationOption",
    "marketSegmentFilter",
    "marketSegmentOption",
    "row",
    "showMoreButton",
    "locationOption",
    "locationFilter"
  ];

  connect() {
    this.visibleCount = 5;
    this.pageSize = 5;
    this.rowTargets.forEach(row => row.style.display = "none");
    this.outsideClickHandler = this.closeOnOutside.bind(this);
    document.addEventListener("click", this.outsideClickHandler);
    this.updateTable();
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler);
  }

  toggle(event) {
    const filter = event.currentTarget.closest(".filter");
    filter.classList.toggle("open");
  }

  search(event) {
    const filter = event.target.closest(".filter");
    const term = event.target.value.toLowerCase();
    const items = filter.querySelectorAll("li");
    items.forEach(li => li.classList.remove("hide-option"));
    items.forEach(li => {
      if (li.style.display === "none") return;
      const haystack = (li.dataset.search || li.innerText).toLowerCase();
      if (!haystack.includes(term)) {
        li.classList.add("hide-option");
      }
    });
  }


  alloyGroupChanged() {
    const selectedGroups = this.alloyGroupOptionTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    this.alloyOptionTargets.forEach(cb => {
      const li = cb.closest("li");
      const group = li.dataset.alloyGroup;

      if (!selectedGroups.length || selectedGroups.includes(group)) {
        li.style.display = "";
      } else {
        li.style.display = "none";
        cb.checked = false;
      }
    });
    this.visibleCount = this.pageSize;
    this.updateTable();
  }


  alloyChanged() {
    this.visibleCount = this.pageSize;
    this.updateTable();
  }

  applicationChanged() {
    const selectedApplications = this.applicationOptionTargets
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    this.marketSegmentOptionTargets.forEach(cb => {
      const li = cb.closest("li");
      const related = (li.dataset.applications || "")
        .split(",")
        .map(s => s.trim())
        .filter(Boolean);

      if (
        selectedApplications.length === 0 ||
        selectedApplications.some(app => related.includes(app))
      ) {
        li.style.display = "";
      } else {
        li.style.display = "none";
        cb.checked = false;
      }
    });
    this.visibleCount = this.pageSize;
    this.updateTable();
  }

  marketSegmentChanged() {
    this.visibleCount = this.pageSize;
    this.updateTable();
  }

  updateTable() {
    const allowed = this.allowedAlloys;
    let results = 0;

    this.rowTargets.forEach(row => {
      const alloy = row.getAttribute("alloy");
      const isAllowed = allowed.includes(alloy);
      const shouldShow = isAllowed && results < this.visibleCount;

      row.style.display = shouldShow ? "" : "none";

      if (isAllowed) results++;
    });
    this.showMoreButtonTarget.style.display = this.visibleCount < results ? "" : "none";
  }

  locationChanged() {
    this.visibleCount = this.pageSize;
    this.updateTable();
  }

  closeOnOutside(event) {
    const filterTargets = [
      this.alloyFilterTarget,
      this.alloyGroupFilterTarget,
    ];
    if (this.hasLocationFilterTarget) {
      filterTargets.push(this.locationFilterTarget);
    }
    if (this.hasApplicationFilterTarget) {
      filterTargets.push(this.applicationFilterTarget);
    }
    if (this.hasMarketSegmentFilterTarget) {
      filterTargets.push(this.marketSegmentFilterTarget);
    }
    filterTargets.forEach(filter => {
      if (!filter.contains(event.target)) {
        filter.classList.remove("open");
      }
    });
  }

  get allowedAlloys() {
    const visibleAlloyOptions = this.alloyOptionTargets.filter(cb =>
      cb.closest("li").style.display !== "none"
    );

    const selectedAlloys = visibleAlloyOptions
      .filter(cb => cb.checked)
      .map(cb => cb.value);

    let allowed = selectedAlloys.length > 0
      ? selectedAlloys
      : visibleAlloyOptions.map(cb => cb.value);

    const selectedLocations = this.locationOptionTargets.filter(cb => cb.checked);

    if (selectedLocations.length > 0) {
      const locationAlloys = selectedLocations.flatMap(cb =>
        (cb.dataset.alloys || "")
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      );
      allowed = allowed.filter(alloy =>
        locationAlloys.includes(alloy)
      );
    }

    const selectedApplicationCbs = this.applicationOptionTargets.filter(cb => cb.checked);
    if (selectedApplicationCbs.length > 0) {
      const applicationAlloys = selectedApplicationCbs.flatMap(cb =>
        (cb.dataset.alloys || "")
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      );
      allowed = allowed.filter(alloyId => applicationAlloys.includes(alloyId));
    }

    const visibleMarketSegmentOptions = this.marketSegmentOptionTargets.filter(
      cb => cb.closest("li").style.display !== "none"
    );
    const selectedSegmentCbs = visibleMarketSegmentOptions.filter(cb => cb.checked);
    if (selectedSegmentCbs.length > 0) {
      const segmentAlloys = selectedSegmentCbs.flatMap(cb =>
        (cb.dataset.alloys || "")
          .split(",")
          .map(s => s.trim())
          .filter(Boolean)
      );
      allowed = allowed.filter(alloyId => segmentAlloys.includes(alloyId));
    }

    return allowed;
  }


  showMore() {
    this.visibleCount += this.pageSize;
    this.updateTable();
  }
}
